import { state } from '../state';
import { iconHome, iconPlus, iconMenu, iconLogIn, iconSettings, iconCopy, iconLogOut, iconBell, iconHash } from '../icons';
import { showContextMenu, closeContextMenu } from '../components/context-menu';
import type { ContextMenuItem } from '../components/context-menu';
import { openCreateServerWizard } from '../components/create-server-wizard';

type SwitchServerFn = (id: string | null) => void;
type CreateServerFn = () => void;
type JoinServerFn = () => void;
type ManageMembersFn = (serverId: string) => void;
type ServerSettingsFn = (serverId: string) => void;
type AppSettingsFn = () => void;
type LeaveServerFn = (serverId: string) => void;

export interface ServerRailCallbacks {
  onSwitchServer: SwitchServerFn;
  onCreateServer: CreateServerFn;
  onJoinServer: JoinServerFn;
  onManageMembers: ManageMembersFn;
  onServerSettings: ServerSettingsFn;
  onAppSettings: AppSettingsFn;
  onLeaveServer: LeaveServerFn;
}

export function renderServerRail(
  container: HTMLElement,
  callbacks: ServerRailCallbacks,
): void {
  // Discord parity: only render servers the current user is a member of.
  // The /api/servers endpoint returns ALL servers (including bot personal
  // spaces — Architect Space, CC Space, dev1 Space) which were polluting
  // the rail. Filter client-side using state.serverMembers as the source
  // of truth for "is this user in this server". Fallback: if we don't
  // know the owner yet (early boot), show everything so we don't render
  // an empty rail.
  const myId = state.ownerId;
  const myServerIds = myId
    ? new Set(state.serverMembers.filter(m => m.person_id === myId).map(m => m.server_id))
    : null;
  const servers = myServerIds
    ? state.servers.filter(s => myServerIds.has(s.id))
    : state.servers;
  const current = state.currentServer;

  let html = '';

  // Hamburger ONLY exists on mobile — opens the channel-list drawer (handled by
  // app.ts's delegated touch/click handler). On desktop the server rail and
  // channel list are always visible, so the icon is suppressed entirely (1c).
  html += `<div class="sr-icon sr-hamburger sr-mobile-only" data-tooltip="Menu" aria-label="Open menu">${iconMenu()}</div>`;

  html += '<div class="sr-separator sr-mobile-only"></div>';

  // Overview / Home icon
  // Mobile/desktop bug fix (cc 2026-04-25): the `.sr-icon` parent has
  // `overflow: hidden` (needed to clip avatar images to the round shape),
  // which CLIPPED any badge positioned at `bottom:-2px; right:-2px`.
  // Result: unread/DM count pills were invisible on every server with
  // pending messages — Discord-mode users couldn't see new-message hints.
  // Wrap the icon in `.sr-icon-wrap` (position:relative, no overflow),
  // and put the badge as a sibling so it can extend past the icon edge.
  const dmBadge = dmUnreadCount > 0 ? `<span class="sr-dm-badge">${dmUnreadCount > 9 ? '9+' : dmUnreadCount}</span>` : '';
  html += `<div class="sr-icon-wrap"><div class="sr-icon sr-overview ${current === null ? 'active' : ''}" data-server="overview" data-tooltip="Home" aria-label="Home">${iconHome()}</div>${dmBadge}</div>`;

  if (servers.length > 0) {
    html += '<div class="sr-separator"></div>';
  }

  // Server icons with role badges + unread indicators
  // Drag-reorder behind feature flag `hp_drag_reorder` — needs the
  // `PUT /api/servers/reorder` endpoint dev2 is adding.
  const dragOn = !!state.flags['hp_drag_reorder'];
  const draggableAttr = dragOn ? ' draggable="true"' : '';
  for (const s of servers) {
    const isActive = current === s.id;
    // Discord does NOT show role badges on server icons — removed (was
    // rendering as a small yellow/blue circle that Mitchell flagged as
    // "emoji face" overlay). Roles are shown in member-list + profile cards.
    const hasUnread = !isActive && serverHasUnread(s.id);
    const mentions = serverMentions[s.id] ?? 0;
    const mentionBadge = mentions > 0
      ? `<span class="sr-badge sr-mention-badge" aria-label="${mentions} unread mentions">${mentions > 99 ? '99+' : mentions}</span>`
      : '';
    const iconContent = s.icon && s.icon.startsWith('/')
      ? `<img src="${escAttr(s.icon)}" style="width:100%;height:100%;object-fit:cover" alt="">`
      : escHtml(s.icon || s.name.charAt(0).toUpperCase());
    const isMirror = s.is_mirror === 1;
    const mirrorAttr = isMirror ? ' data-mirror="1"' : '';
    const mirrorClass = isMirror ? ' sr-mirror' : '';
    const subtitleSuffix = s.subtitle ? ` — ${s.subtitle}` : '';
    const titleText = isMirror ? `${s.name} (Mirror — read-only)` : `${s.name}${subtitleSuffix}`;
    html += `<div class="sr-icon-wrap" data-server-row="${escAttr(s.id)}"${draggableAttr}><div class="sr-icon${mirrorClass} ${isActive ? 'active' : ''} ${hasUnread ? 'has-unread' : ''}" data-server="${escAttr(s.id)}"${mirrorAttr} data-tooltip="${escAttr(titleText)}" aria-label="${escAttr(titleText)}">${iconContent}</div>${mentionBadge}</div>`;
  }

  // Bottom group divider (Discord parity) — separates the server list from the
  // +/Discover/settings cluster below.
  html += '<div class="sr-separator sr-divider-bottom"></div>';

  // Create server (dashed circle with + icon)
  html += `<div class="sr-icon sr-create" data-tooltip="Create Team" aria-label="Create Team">${iconPlus()}</div>`;

  // Join server
  html += `<div class="sr-icon sr-join" data-tooltip="Join Team" aria-label="Join Team">${iconLogIn()}</div>`;

  // Discover public servers (Discord parity \u2014 compass icon).
  html += `<div class="sr-icon sr-discover" data-tooltip="Discover Public Servers" aria-label="Discover Public Servers"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg></div>`;

  // Spacer + collapse toggle (1d) — desktop only. Persisted in localStorage so
  // the rail stays in whichever state the user last chose.
  html += '<div class="sr-grow"></div>';
  const isCollapsed = document.documentElement.classList.contains('rail-collapsed');
  html += `<button class="sr-collapse-toggle sr-desktop-only" title="${isCollapsed ? 'Expand server rail' : 'Collapse server rail'}" aria-label="${isCollapsed ? 'Expand server rail' : 'Collapse server rail'}" aria-pressed="${isCollapsed}">
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="transform:rotate(${isCollapsed ? '180' : '0'}deg);transition:transform var(--dur-fast) var(--ease-out)">
      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
    </svg>
  </button>`;

  container.innerHTML = html;

  // Event listeners: server icons
  container.querySelectorAll<HTMLElement>('[data-server]').forEach(el => {
    el.addEventListener('click', () => {
      const sid = el.dataset['server'];
      if (sid === 'overview') callbacks.onSwitchServer(null);
      else if (sid) callbacks.onSwitchServer(sid);
    });

    // Right-click context menu on server icons
    const sid = el.dataset['server'];
    if (sid && sid !== 'overview') {
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        showServerContextMenu(e.clientX, e.clientY, sid, callbacks);
      });
      el.addEventListener('auxclick', (e) => {
        if (e.button === 1) { e.preventDefault(); openServerInNewTab(sid); }
      });
    }
  });

  // Create-server entry: route through the new template-picker wizard
  // (Mitchell ask 2026-04-25). The legacy `callbacks.onCreateServer()`
  // is kept on the interface for backwards compat with other entry points
  // (home page) but the rail's `+` icon — the primary surface — opens the
  // wizard directly so we don't have to thread it through app.ts.
  container.querySelector('.sr-create')?.addEventListener('click', () => {
    openCreateServerWizard({
      ownerId: state.ownerId ?? state.people[0]?.id ?? '',
      onCreated: (serverId: string) => {
        window.dispatchEvent(new CustomEvent('hub-servers-changed'));
        callbacks.onSwitchServer(serverId);
      },
    });
  });
  container.querySelector('.sr-join')?.addEventListener('click', () => callbacks.onJoinServer());
  container.querySelector('.sr-discover')?.addEventListener('click', () => void openDiscoverServers(callbacks));

  // Hamburger only does something on mobile (open channel-list drawer via the
  // delegated handler in app.ts). On desktop the icon is hidden via CSS, so no
  // click handler is needed here. The legacy desktop dropdown was killed (1c).
  void callbacks;

  // Server-rail collapse toggle (1d) — bottom of rail, keyboard-accessible.
  const collapseBtn = container.querySelector('.sr-collapse-toggle');
  collapseBtn?.addEventListener('click', () => {
    const collapsed = !document.documentElement.classList.contains('rail-collapsed');
    document.documentElement.classList.toggle('rail-collapsed', collapsed);
    localStorage.setItem('hub-people-rail-collapsed', String(collapsed));
  });

  // Drag-reorder (Discord parity, behind hp_drag_reorder flag). Reorders the
  // visible server icons via HTML5 D&D, then POSTs the new order to dev2's
  // `PUT /api/servers/reorder` endpoint. Local state.servers is updated
  // optimistically so the rail stays in the new order even if the request is
  // still in flight when re-render happens.
  if (dragOn) {
    let dragSrcId: string | null = null;
    container.querySelectorAll<HTMLElement>('[data-server-row]').forEach(row => {
      row.addEventListener('dragstart', (e) => {
        dragSrcId = row.dataset['serverRow'] ?? null;
        if (e.dataTransfer && dragSrcId) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', dragSrcId);
        }
        row.classList.add('sr-dragging');
      });
      row.addEventListener('dragend', () => {
        row.classList.remove('sr-dragging');
        container.querySelectorAll('.sr-drop-target').forEach(el => el.classList.remove('sr-drop-target'));
      });
      row.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        row.classList.add('sr-drop-target');
      });
      row.addEventListener('dragleave', () => row.classList.remove('sr-drop-target'));
      row.addEventListener('drop', (e) => {
        e.preventDefault();
        row.classList.remove('sr-drop-target');
        const dstId = row.dataset['serverRow'];
        if (!dragSrcId || !dstId || dragSrcId === dstId) return;
        const order = Array.from(container.querySelectorAll<HTMLElement>('[data-server-row]'))
          .map(r => r.dataset['serverRow'] ?? '')
          .filter(Boolean);
        const fromIdx = order.indexOf(dragSrcId);
        const toIdx = order.indexOf(dstId);
        if (fromIdx < 0 || toIdx < 0) return;
        order.splice(toIdx, 0, ...order.splice(fromIdx, 1));
        // Optimistic update
        const ordered: typeof state.servers = [];
        for (const id of order) {
          const s = state.servers.find(ss => ss.id === id);
          if (s) ordered.push(s);
        }
        // Append any servers not in the visible list (e.g. bot personal spaces)
        for (const s of state.servers) if (!ordered.includes(s)) ordered.push(s);
        state.servers = ordered;
        renderServerRail(container, callbacks);
        void import('../api').then(({ put }) => {
          void put('/api/servers/reorder', { order }).catch(() => {/* dev2 not deployed yet */});
        });
        dragSrcId = null;
      });
    });
  }
}

function openServerInNewTab(serverId: string): void {
  const url = `${window.location.origin}${window.location.pathname}#s/${serverId}`;
  window.open(url, '_blank', 'noopener');
}

function showServerContextMenu(x: number, y: number, serverId: string, cb: ServerRailCallbacks): void {
  const newTabIcon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
  const items: ContextMenuItem[] = [
    { label: 'Open in New Tab', icon: newTabIcon, action: () => openServerInNewTab(serverId) },
    { label: 'Server Settings', icon: iconSettings(), action: () => cb.onServerSettings(serverId) },
    { label: 'Create Channel', icon: iconHash(), action: () => {
      // Bounce through server-settings \u2192 channels tab so users always have a
      // canonical create flow. Channel-list also has an inline + per category.
      cb.onServerSettings(serverId);
    } },
    { label: 'Notification Settings', icon: iconBell(), action: () => {
      void openServerNotifModal(serverId);
    } },
    { label: 'Copy Server ID', icon: iconCopy(), action: () => { void navigator.clipboard.writeText(serverId); } },
    { label: '', divider: true },
    { label: 'Leave Server', icon: iconLogOut(), danger: true, action: () => cb.onLeaveServer(serverId) },
  ];

  showContextMenu(x, y, items);
}

/** Discord-style server notification settings sheet. Three radio modes:
 *  All Messages / Only @mentions / Nothing. Wraps the existing
 *  setServerNotifMode helper so chat.ts notify() picks up the change
 *  immediately, no reload. */
async function openServerNotifModal(serverId: string): Promise<void> {
  const { getServerNotifMode, setServerNotifMode, isNotificationsMasterOn, setNotificationsMasterOn } =
    await import('../components/notifications');
  const server = state.servers.find(s => s.id === serverId);
  const name = server?.name || 'Server';
  const cur = getServerNotifMode(serverId);
  const masterOn = isNotificationsMasterOn();

  document.querySelectorAll('.snm-modal, .snm-backdrop').forEach(el => el.remove());

  const backdrop = document.createElement('div');
  backdrop.className = 'snm-backdrop';
  document.body.appendChild(backdrop);

  const modal = document.createElement('div');
  modal.className = 'snm-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', `${name} notification settings`);
  modal.innerHTML = `
    <div class="snm-header">
      <div class="snm-title">${escHtml(name)} \u2014 Notification Settings</div>
      <button class="snm-close" aria-label="Close">&times;</button>
    </div>
    <div class="snm-body">
      <label class="snm-toggle">
        <input type="checkbox" id="snm-master" ${masterOn ? 'checked' : ''}>
        <span>Notifications enabled (master)</span>
      </label>
      <div class="snm-divider"></div>
      <div class="snm-section-title">Server Notification Settings</div>
      <label class="snm-radio">
        <input type="radio" name="snm-mode" value="all" ${cur === 'all' ? 'checked' : ''}>
        <span class="snm-radio-label">
          <strong>All Messages</strong>
          <span class="snm-radio-sub">Every new message in this server.</span>
        </span>
      </label>
      <label class="snm-radio">
        <input type="radio" name="snm-mode" value="mentions" ${cur === 'mentions' ? 'checked' : ''}>
        <span class="snm-radio-label">
          <strong>Only @mentions</strong>
          <span class="snm-radio-sub">Only when someone @mentions you, replies to you, or DMs.</span>
        </span>
      </label>
      <label class="snm-radio">
        <input type="radio" name="snm-mode" value="none" ${cur === 'none' ? 'checked' : ''}>
        <span class="snm-radio-label">
          <strong>Nothing</strong>
          <span class="snm-radio-sub">Suppress all notifications from this server (DMs unaffected).</span>
        </span>
      </label>
    </div>
    <div class="snm-footer">
      <button class="btn btn-sm btn-secondary" id="snm-cancel">Cancel</button>
      <button class="btn btn-sm btn-primary" id="snm-save">Save</button>
    </div>
  `;
  document.body.appendChild(modal);

  const close = (): void => { modal.remove(); backdrop.remove(); };
  backdrop.addEventListener('click', close);
  modal.querySelector('.snm-close')?.addEventListener('click', close);
  modal.querySelector('#snm-cancel')?.addEventListener('click', close);
  modal.querySelector('#snm-save')?.addEventListener('click', () => {
    const masterEl = modal.querySelector<HTMLInputElement>('#snm-master');
    if (masterEl) setNotificationsMasterOn(masterEl.checked);
    const checked = modal.querySelector<HTMLInputElement>('input[name="snm-mode"]:checked');
    if (checked) setServerNotifMode(serverId, checked.value as 'all' | 'mentions' | 'none');
    close();
  });
  document.addEventListener('keydown', function esc(e) {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); }
  });
}

// (escHtml already defined further down — reuse it)

/** Track which servers have unread messages */
const unreadServers = new Set<string>();
let dmUnreadCount = 0;
/** Per-server mention counts for the numeric badge (Discord parity). */
const serverMentions: Record<string, number> = {};

export function setDmUnreadCount(count: number): void {
  dmUnreadCount = count;
}

export function setServerUnread(serverId: string, unread: boolean): void {
  if (unread) unreadServers.add(serverId);
  else unreadServers.delete(serverId);
}

export function setServerMentions(serverId: string, count: number): void {
  if (count > 0) serverMentions[serverId] = count;
  else delete serverMentions[serverId];
}

function serverHasUnread(serverId: string): boolean {
  return unreadServers.has(serverId);
}


function escHtml(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// Re-export closeContextMenu for external use
export { closeContextMenu };

/** Discover Public Servers \u2014 Discord parity. Lists every server with
 *  visibility='public' from /api/servers/discover, ranked by member count.
 *  Each row gets a Join button that POSTs /api/servers/{id}/members/add
 *  for the current user. Modal style matches the Server Notification
 *  modal (snm-) so the visual language stays consistent. */
async function openDiscoverServers(cb: ServerRailCallbacks): Promise<void> {
  void cb;
  document.querySelectorAll('.disc-modal, .disc-backdrop').forEach(el => el.remove());

  const backdrop = document.createElement('div');
  backdrop.className = 'disc-backdrop snm-backdrop';
  document.body.appendChild(backdrop);

  const modal = document.createElement('div');
  modal.className = 'disc-modal snm-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Discover public servers');
  modal.innerHTML = `
    <div class="snm-header">
      <div class="snm-title">Discover Public Servers</div>
      <button class="snm-close" aria-label="Close">&times;</button>
    </div>
    <div class="disc-search-row">
      <svg class="disc-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <input type="search" id="disc-search" class="disc-search-input" placeholder="Search servers" aria-label="Search public servers" autocomplete="off" />
    </div>
    <div class="snm-body disc-body" id="disc-body">
      <div class="disc-loading" aria-hidden="true">
        <div class="disc-skel"></div>
        <div class="disc-skel"></div>
        <div class="disc-skel"></div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const close = (): void => { modal.remove(); backdrop.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  backdrop.addEventListener('click', close);
  modal.querySelector('.snm-close')?.addEventListener('click', close);

  const CHECK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';

  try {
    const { get, post } = await import('../api');
    type DiscoverRow = { id: string; name: string; icon: string; description: string; member_count: number };
    const list = await get<DiscoverRow[]>('/api/servers/discover');
    const myId = state.ownerId || '';
    const myServerIds = new Set(state.serverMembers.filter(m => m.person_id === myId).map(m => m.server_id));

    const body = modal.querySelector<HTMLDivElement>('#disc-body');
    if (!body) return;

    if (list.length === 0) {
      body.innerHTML = '<div class="disc-empty">No public servers yet. Owners can flip a server\u2019s visibility to <code>public</code> in Server Settings.</div>';
      modal.querySelector<HTMLInputElement>('#disc-search')?.setAttribute('disabled', 'true');
      return;
    }

    const cardMarkup = (srv: DiscoverRow): string => {
      const joined = myServerIds.has(srv.id);
      const initial = (srv.name || '?').charAt(0).toUpperCase();
      const desc = srv.description || 'No description.';
      const memLabel = `${srv.member_count} member${srv.member_count === 1 ? '' : 's'}`;
      const btnClass = joined ? 'disc-card-btn disc-card-btn-joined' : 'disc-card-btn disc-card-btn-join';
      const btnInner = joined ? `${CHECK_SVG}<span>Joined</span>` : 'Join';
      const disabledAttr = joined ? 'disabled aria-disabled="true"' : '';
      return `
        <div class="disc-card" data-server-id="${escAttr(srv.id)}">
          <div class="disc-card-icon">${escHtml(srv.icon || initial)}</div>
          <div class="disc-card-body">
            <div class="disc-card-name">${escHtml(srv.name)}</div>
            <div class="disc-card-desc">${escHtml(desc)}</div>
            <div class="disc-card-meta">
              <span class="disc-card-meta-dot" aria-hidden="true"></span>
              <span>${escHtml(memLabel)}</span>
            </div>
          </div>
          <button class="${btnClass}" data-action="join" data-server="${escAttr(srv.id)}" ${disabledAttr}>${btnInner}</button>
        </div>
      `;
    };

    const wireJoinButtons = (): void => {
      body.querySelectorAll<HTMLButtonElement>('[data-action="join"]').forEach(btn => {
        if (btn.dataset['wired'] === '1') return;
        btn.dataset['wired'] = '1';
        btn.addEventListener('click', async () => {
          if (btn.disabled) return;
          const sid = btn.dataset['server'];
          if (!sid || !myId) return;
          btn.disabled = true;
          btn.textContent = 'Joining\u2026';
          try {
            await post(`/api/servers/${encodeURIComponent(sid)}/members/add`, { person_id: myId, role: 'member' });
            myServerIds.add(sid);
            btn.className = 'disc-card-btn disc-card-btn-joined';
            btn.setAttribute('aria-disabled', 'true');
            btn.innerHTML = `${CHECK_SVG}<span>Joined</span>`;
            const { showToast: toast } = await import('../components/toast');
            toast('Joined server', 'ok');
            window.dispatchEvent(new CustomEvent('hub-servers-changed'));
          } catch {
            btn.disabled = false;
            btn.className = 'disc-card-btn disc-card-btn-join';
            btn.textContent = 'Join';
            const { showToast: toast } = await import('../components/toast');
            toast('Failed to join \u2014 server may be invite-only', 'err');
          }
        });
      });
    };

    const renderList = (filtered: DiscoverRow[]): void => {
      if (filtered.length === 0) {
        body.innerHTML = '<div class="disc-empty">No servers match your search.</div>';
        return;
      }
      body.innerHTML = filtered.map(cardMarkup).join('');
      wireJoinButtons();
    };

    renderList(list);

    const searchEl = modal.querySelector<HTMLInputElement>('#disc-search');
    if (searchEl) {
      searchEl.addEventListener('input', () => {
        const q = searchEl.value.trim().toLowerCase();
        if (!q) { renderList(list); return; }
        const filtered = list.filter(s =>
          s.name.toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q)
        );
        renderList(filtered);
      });
    }
  } catch {
    const body = modal.querySelector('#disc-body');
    if (body) body.innerHTML = '<div class="disc-empty">Failed to load public servers. Try again later.</div>';
    modal.querySelector<HTMLInputElement>('#disc-search')?.setAttribute('disabled', 'true');
  }
}
